import React, { Component } from 'react';
import { Modal, Row, Col, Tab } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import { inject, observer } from 'mobx-react';
import { CellUnits, ViewTypes } from '../../../components/Scheduler';
import Scheduler, { DATE_FORMAT, SchedulerData } from './RosterScheduler'
import moment from 'moment';

//components
import applicationRouter from '../../../hoc/applicationRouter';
import withLocalization from '../../../hoc/withLocalization';
import { CurrentUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import Select from '../../../elements/Select';
import CustomMultiSelect from '../../../elements/CustomMultiSelect';
import LoadingSpinner from '~/elements/LoadingSpinner';
import AlertMessage, { createAlert } from '~/components/Alert/AlertMessage.jsx';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import deleteIcon from '~/assets/img/deleting.svg';

//utils
import { minutesToTimeInput } from '../../../utils/timeCalc';

const getUserOptions = (resources) => {
    return resources.map((user) => ({
        label: user.name,
        value: user.id,
        employee_type: user.employee_type,
        isAvailable: true
    }));
}

const getRosterTemplateOptions = (rosterTemplates, dateTimeRules) => {
    return rosterTemplates.map((template) => ({
        status: template.status,
        label: `${template.name} (${moment(template.cycle_start_date).format(dateTimeRules.short_date_format)} - ${moment(template.cycle_end_date).format(dateTimeRules.short_date_format)})`,
        value: template.id,
        employee_type: template.department,
        cycle_start_date: template.cycle_start_date,
        cycle_end_date: template.cycle_end_date,
        employeeIds: template.employeeIds,
        cycle_totals: template.cycle_totals
    }));
}

const generateEventsFromRosterTemplates = (rosterTemplates) => {
    if (!Array.isArray(rosterTemplates)) return [];

    return rosterTemplates.flatMap(template => {
        const { id: templateId, shift_data = {} } = template;
        const shiftDetails = (template.rules && template.rules.shiftDetails) || {};
        const events = [];

        Object.entries(shift_data).forEach(([date, { shiftId }]) => {
            const shift = Object.values(shiftDetails).find(s => s.id === shiftId);
            if (!shift || shift.start_time == null || shift.end_time == null) return;

            const dayOfWeek = moment(date).format('dddd').toLowerCase();
            const eventId = `${templateId}-${shiftId}-${dayOfWeek}`;
            const formattedDate = moment(date).format('YYYY-MM-DD');
            const start = `${formattedDate} ${minutesToTimeInput(shift.start_time, false)}:00`;
            const end = `${formattedDate} ${minutesToTimeInput(shift.end_time, false)}:00`;

            events.push({
                id: eventId,
                resourceId: templateId,
                title: shift.shift_code,
                start,
                end,
                startTime: shift.start_time,
                endTime: shift.end_time,
                for_date: date,
                shiftId,
                templateId
            });
        });

        return events;
    });
};

class RosterShiftViewModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            isLoading: true,
            resources: [],
            rosterTemplates: [],
            departmentOptions: [],
            selectedDepartment: null,
            rosterTemplatesByDepartmentOptions: [],
            selectedRosterTemplate: null,
            usersOptionsWithoutFilter: [],
            usersByDepartmentOptions: [],
            selectedUserIds: [],
            selectedUsers: [],
            schedulerData: null,
            width: 0,
            events: [],
            resourcesTemplate: [],
            user_group: null,
            currentTab: 'active',
            alertMessages: []
        };
        this.scheduleRef = React.createRef();
    }

    componentDidMount() {
        this.loadData();
        window.addEventListener('resize', this.updateWidth);
    }

    componentDidUpdate(_, prevState) {
        if (prevState.isLoading && !this.state.isLoading) {
            this.updateWidth();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWidth);
    }

    updateWidth = () => {
        if (this.scheduleRef.current) {
            this.setState({ width: this.scheduleRef.current.offsetWidth });
        }
    };

    removeAlert = (messages) => {
        this.setState({ alertMessages: messages });
    }

    loadData = async () => {
        const { scheduleStore, commonStore, rosterStore, userStore } = this.props;
        const { resources } = scheduleStore;
        const dateTimeRules = commonStore.getDateTimeRules();

        const departments = commonStore?.config?.client?.data?.roles
        const departmentOptions = departments.map((department) => ({ label: department.name, value: department.code }))
        departmentOptions.unshift({ label: 'Others', value: 'z_none' })

        let pmMembers = '';
        if (userStore?.currentUser.user_type !== 'admin' && userStore?.currentUser?.Manages?.length) {
            pmMembers = userStore?.currentUser.Manages.join(',');
        }

        const { activeTemplates, publishedTemplates } = await rosterStore.getRosterTemplates({ pmMembers });

        const rosterTemplates = [...activeTemplates, ...publishedTemplates]

        const resourcesTemplate = rosterTemplates.map(temp => ({ id: temp.id, name: temp.name }));
        const events = generateEventsFromRosterTemplates(rosterTemplates);
        const usersOptionsWithoutFilter = getUserOptions(resources);
        const rosterTemplatesByDepartmentOptions = getRosterTemplateOptions(rosterTemplates, dateTimeRules).filter(
            template => template.employee_type === departmentOptions[0].value
        );

        this.setState({
            resources,
            rosterTemplates,
            usersOptionsWithoutFilter,
            departmentOptions,
            rosterTemplatesByDepartmentOptions,
            selectedDepartment: departmentOptions[0].value,
            resourcesTemplate, events
        }, () => {
            this.setState({ isLoading: false });
            this.initRosterScheduler();
        })
    }

    initRosterScheduler = () => {
        const { t } = this.props;
        const { resourcesTemplate, events } = this.state;
        let rosterSchedulerData = new SchedulerData(
            moment().format('YYYY-MM-DD'),
            ViewTypes.Custom1,
            false,
            false,
            {
                headerEnabled: false,
                startResizable: false,
                endResizable: false,
                movable: false,
                creatable: false,
                resourceName: `${t('Template')} ${t('Name')}`,
                nonAgendaOtherCellHeaderFormat: 'ddd | D',
                schedulerWidth: '78%',
                tableHeaderHeight: 75,
                eventItemHeight: 40,
                eventItemLineHeight: 40,
                yearCellWidth: 60,
            },
            { getCustomDateFunc: this.getCustomDate },
        );
        rosterSchedulerData.localeMoment.locale('en');
        rosterSchedulerData.setResources(resourcesTemplate.filter(r => r.id === this.state.selectedRosterTemplate));
        rosterSchedulerData.setEvents(events);
        this.setState({ schedulerData: rosterSchedulerData })
    }

    getCustomDate = (schedulerData, num, date = undefined) => {
        const { viewType } = schedulerData;
        let selectDate = schedulerData.startDate;
        let { rosterTemplatesByDepartmentOptions, selectedRosterTemplate } = this.state;

        if (date != undefined)
            selectDate = date;

        let startDate = num === 0 ? selectDate :
            schedulerData.localeMoment(selectDate).add(2 * num, 'days').format(DATE_FORMAT),
            endDate = schedulerData.localeMoment(startDate).add(1, 'days').format(DATE_FORMAT),
            cellUnit = CellUnits.Hour;
        if (viewType === ViewTypes.Custom1) {
            startDate = moment(rosterTemplatesByDepartmentOptions.find(temp => temp.value === selectedRosterTemplate)?.cycle_start_date).format(DATE_FORMAT);
            endDate = moment(rosterTemplatesByDepartmentOptions.find(temp => temp.value === selectedRosterTemplate)?.cycle_end_date).format(DATE_FORMAT);
            cellUnit = CellUnits.Day;
        } else if (viewType === ViewTypes.Custom2) {
            let firstDayOfMonth = schedulerData.localeMoment(selectDate).startOf('month').format(DATE_FORMAT);
            startDate = num === 0 ? firstDayOfMonth : schedulerData.localeMoment(firstDayOfMonth).add(2 * num, 'months').format(DATE_FORMAT);
            endDate = schedulerData.localeMoment(startDate).add(1, 'months').endOf('month').format(DATE_FORMAT);
            cellUnit = CellUnits.Day;
        }

        return {
            startDate,
            endDate,
            cellUnit
        };
    }

    handleUserSelection = (selectedIds) => {
        const { usersByDepartmentOptions } = this.state;
        const selectedUsers = usersByDepartmentOptions.filter(user => selectedIds.includes(user.value));
        this.setState({
            selectedUserIds: selectedIds,
            selectedUsers: selectedUsers
        });
    }

    handleDeleteTemplate = (res) => {
        const { handleClose, reload } = this.props;
        if (res?.error)
            this.setState({ alertMessages: [createAlert(res.message, 'error')] });
        else {
            handleClose();
            reload();
        }
    }

    handleRemoveUser = (userId) => {
        const { t, handleClose, reload, rosterStore } = this.props;
        const { selectedUserIds, selectedUsers, selectedRosterTemplate, currentTab } = this.state;
        const remaniningUsers = selectedUserIds.filter(id => id !== userId);
        if (!remaniningUsers.length) {
            const isPublishedTemplate = currentTab === 'published';
            const isPublishedMessage = `${t('Note')}- ${'This action will also delete the future shifts created with this roster plan'}.`
            confirmAlert({
                title: t('Are you sure?'),
                message: `${t('Removing all the members will delete the entire template. Are you sure?')}${isPublishedTemplate ? `\n${isPublishedMessage}` : ''}`,
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: () => {
                            return rosterStore.deleteTemplate(selectedRosterTemplate).then((res) => {
                                this.handleDeleteTemplate(res);
                            })
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => { }
                    }
                ],
            });
        }
        else {
            this.setState({
                selectedUserIds: remaniningUsers,
                selectedUsers: selectedUsers.filter(user => user.value !== userId)
            });
        }
    }

    handleChangeAndValidate = async (name, value) => {
        if (name === 'selectedDepartment') {
            const { resources, rosterTemplates } = this.state;
            const { commonStore } = this.props;
            const dateTimeRules = commonStore.getDateTimeRules();

            const usersByDepartmentOptions = getUserOptions(resources).filter(user => user.employee_type === value);
            const rosterTemplatesByDepartmentOptions = getRosterTemplateOptions(rosterTemplates, dateTimeRules).filter(
                template => template.employee_type === value
            );

            this.setState({
                usersByDepartmentOptions,
                rosterTemplatesByDepartmentOptions,
                selectedUserIds: [],
                selectedUsers: [],
                selectedRosterTemplate: null,
                [name]: value
            });

        } else if (name === 'selectedRosterTemplate') {
            const { rosterTemplates, selectedDepartment } = this.state;

            const { employeeIds, selectedUsers, updatedUsersByDepartmentOptions } = await this.getUsersBySelectedTemplate(selectedDepartment, [], null, rosterTemplates, value)

            this.setState({
                selectedRosterTemplate: value,
                selectedUserIds: employeeIds,
                selectedUsers,
                usersByDepartmentOptions: updatedUsersByDepartmentOptions
            }, () => {
                this.initRosterScheduler()
            });
        } else if (name === 'user_group') {
            const { rosterTemplates, selectedRosterTemplate, selectedDepartment } = this.state;
            const { t, groupDetails } = this.props;
            const { employeeIds, selectedUsers, updatedUsersByDepartmentOptions } = await this.getUsersBySelectedTemplate(selectedDepartment, [], null, rosterTemplates, selectedRosterTemplate)

            if (value > 0) {
                const updatedUsersOptions = updatedUsersByDepartmentOptions.filter(user => (value > 0 ? (!!groupDetails.find(g => g.code === value && g.userIds.includes(user.value))) : true));
                const seletedUserId = updatedUsersOptions.map(user => user.value);
                const notIncludedUsersInGroup = selectedUsers.filter(
                    selectedUser => !updatedUsersOptions.some(groupUser => groupUser.value === selectedUser.value)
                );
                if (notIncludedUsersInGroup.length) {
                    const namesString = notIncludedUsersInGroup.map(user => user.label).join(", ");
                    confirmAlert({
                        title: t('Are you sure?'),
                        message: t(`Few users (${namesString}) of this template is not assigned to this group. Do you want to continue?`),
                        buttons: [
                            {
                                label: t('Yes'),
                                onClick: () => {
                                    this.setState({ selectedUserIds: seletedUserId, selectedUsers: updatedUsersOptions, usersByDepartmentOptions: updatedUsersOptions, user_group: value });
                                },
                            },
                            {
                                label: t('No'),
                                onClick: () => {
                                }
                            }
                        ],
                    });
                } else {
                    this.setState({ selectedUserIds: seletedUserId, selectedUsers: updatedUsersOptions, usersByDepartmentOptions: updatedUsersOptions, user_group: value });
                }
            } else {
                this.setState({ selectedUserIds: employeeIds, selectedUsers: selectedUsers, usersByDepartmentOptions: updatedUsersByDepartmentOptions, user_group: value });
            }
        } else {
            this.setState({ [name]: value });
        }
    };

    getUsersBySelectedTemplate = async (selectedDepartment, rosterTemplatesByDepartmentOptions, userRosterTemplate, rosterTemplates, selectedRosterTemplateId = null) => {
        const { userStore } = this.props;
        // Determine selected template
        const selectedRosterTemplate = selectedRosterTemplateId ? rosterTemplates.find(t => t.id === selectedRosterTemplateId) : rosterTemplates.find(t =>
            t.id === rosterTemplatesByDepartmentOptions.find(opt => opt.value === userRosterTemplate?.roster_template_id)?.value
        );

        if (!selectedRosterTemplate) {
            return { employeeIds: [], selectedUsers: [], updatedUsersByDepartmentOptions: [] };
        }

        const { cycle_start_date, cycle_end_date, employeeIds = [] } = selectedRosterTemplate;

        let pmMembers = '';
        if (userStore?.currentUser.user_type !== 'admin' && userStore?.currentUser?.Manages?.length) {
            pmMembers = userStore?.currentUser.Manages.join(',');
        }

        const response = await this.props.rosterStore.loadResourceList({
            rosterTemplateId: selectedRosterTemplate.id,
            startDate: cycle_start_date,
            endDate: cycle_end_date,
            department: selectedDepartment,
            pmMembers
        });

        const backendSelectedUsers = response?.selectedUsers ?? [];
        const availableResources = response?.resources ?? [];

        const selectedUserOptions = getUserOptions(backendSelectedUsers) ?? [];
        const availableUserOptions = getUserOptions(availableResources) ?? [];

        const userMap = new Map();

        availableUserOptions
            .forEach(user => {
                userMap.set(user.value, {
                    ...user,
                    isAvailable: true,
                    disabled: false
                });
            });

        selectedUserOptions.forEach(user => {
            userMap.set(user.value, {
                ...user,
                isAvailable: true,
                disabled: true
            });
        });

        const updatedUsersByDepartmentOptions = Array.from(userMap.values());

        // Selected users for display in selected list
        const selectedUsers = selectedUserOptions.map(user => ({ ...user, disabled: true }));

        return { employeeIds, selectedUsers, updatedUsersByDepartmentOptions };
    }

    handleSave = async () => {
        const { selectedRosterTemplate, selectedUserIds, user_group } = this.state;
        const { rosterStore, commonStore, handleClose, reload } = this.props;
        const data = {
            selectedRosterTemplate,
            selectedUserIds,
            user_group
        }

        if (!selectedRosterTemplate || !selectedUserIds.length) {
            return commonStore.addNotification(('All field are required!'), null, 'error');
        }

        try {
            await rosterStore.createRosterFromExistingTemplate(data)
            commonStore.addNotification(('Roster created successfully!'), null, 'success');
            reload()
            handleClose()
        } catch (err) {
            console.error(err);
        }
    }

    customEventItemTemplate = (_, eventItem) => {
        return (
            <div style={{ marginX: 'auto', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'black' }}>
                <div style={{ fontSize: '12px' }}>{eventItem.title}</div>
            </div>
        );
    };

    handleDepartmentAndTemplates = (departmentOptions, selectedDepartment, rosterTemplatesByDepartmentOptions, selectedRosterTemplate) => {
        const { t } = this.props;
        return (
            <Row className='d-flex justify-content-center align-items-end'>
                <Col md={6} className='pe-0'>
                    <label className="control-label mb-1">{t('Select')} {t('Department')}</label>
                    <Select
                        options={departmentOptions}
                        value={departmentOptions.find(item => item.value === selectedDepartment)}
                        onChange={e => this.handleChangeAndValidate('selectedDepartment', e.value)}
                    />
                </Col>
                <Col md={6}>
                    <label className="control-label mb-1">{t('Select')} {t('Template')}</label>
                    <Select
                        options={rosterTemplatesByDepartmentOptions}
                        value={selectedRosterTemplate && rosterTemplatesByDepartmentOptions.length && rosterTemplatesByDepartmentOptions.find(item => item.value === selectedRosterTemplate)}
                        onChange={e => this.handleChangeAndValidate('selectedRosterTemplate', e.value)}
                    />
                </Col>
            </Row>
        )
    }

    renderSelectedUsers = () => {
        const { selectedUsers } = this.state;
        return (
            <div className='p-1' style={{ maxHeight: '125px', overflow: 'auto' }}>
                {selectedUsers.map(user => (
                    <div
                        key={user.value}
                        className="d-flex justify-content-between align-items-center p-2"
                        style={{ borderBottom: '1px solid #b5bb9e' }}
                    >
                        <span style={{ fontWeight: 'bold', color: '#2550ac' }}>{user.label}</span>
                        <img
                            src={deleteIcon}
                            className="cursor-pointer"
                            onClick={() => this.handleRemoveUser(user.value)}
                            alt="remove"
                            style={{ width: '16px', height: '16px' }}
                        />
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { commonStore, show, handleClose, t, canManageActive, canDeletePublished } = this.props;
        const { isLoading, usersByDepartmentOptions, selectedUserIds, departmentOptions,
            selectedDepartment, rosterTemplatesByDepartmentOptions, selectedRosterTemplate,
            width, user_group, alertMessages } = this.state;
        let currentTab = this.state.currentTab;
        if (currentTab === 'active' && !canManageActive && canDeletePublished) currentTab = 'published';
        const dateTimeRules = commonStore.getDateTimeRules();

        const _rosterTemplatesByDepartmentOptions = rosterTemplatesByDepartmentOptions.filter(ele => ele.status === currentTab)
        const currentTemplateDetails = _rosterTemplatesByDepartmentOptions.find(item => item.value === selectedRosterTemplate);
        const message = currentTemplateDetails && `${t('Deleting this template will delete the published shifts from')} ${moment().add(1, 'day').format(dateTimeRules.short_date_format)} ${t('to')} ${moment(currentTemplateDetails.cycle_end_date).format(dateTimeRules.short_date_format)}`;

        return (
            <div>
                <Modal
                    size="xl"
                    centered
                    show={show}
                    onHide={handleClose}
                >
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className='w-100 text-center custom-modal-title'>
                            {t('Manage')} {t('Roster template')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            onClick={handleClose}
                            alt='close-btn'
                        />
                    </Modal.Header>
                    <Modal.Body style={{ minHeight: '80vh' }}>
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                                <div>
                                    <AlertMessage messages={alertMessages} onClose={this.removeAlert} maxLimit={2} />
                                    <div className='d-flex px-3'>
                                        <ul className="list-group d-flex flex-row" >
                                            {canManageActive && (
                                                <li className={currentTab === 'active' ? 'list-group-item active cursor-pointer' : 'list-group-item cursor-pointer'}
                                                    onClick={() => this.setState({ currentTab: 'active', selectedRosterTemplate: null, selectedUsers: [] })}>{t('Manage')} {t('Active')}</li>
                                            )}
                                            {canDeletePublished && (
                                                <li className={currentTab === 'published' ? 'list-group-item active cursor-pointer ml-10' : 'list-group-item cursor-pointer ml-10'}
                                                    onClick={() => this.setState({ currentTab: 'published', selectedRosterTemplate: null, selectedUsers: [] })}>{t('Manage')} {t('Published')}</li>
                                            )}
                                        </ul>
                                    </div>
                                    <Tab.Container activeKey={currentTab} id='modal-accept'>
                                        <Tab.Content>
                                            <Tab.Pane eventKey="active">
                                                <div className='px-3' style={{ minHeight: '500px' }}>
                                                    {this.handleDepartmentAndTemplates(departmentOptions, selectedDepartment, _rosterTemplatesByDepartmentOptions, selectedRosterTemplate)}
                                                    <div ref={this.scheduleRef} className='mt-2'>
                                                        {
                                                            selectedRosterTemplate && this.state.schedulerData && (
                                                                <div id='Roster-Scheduler'>
                                                                    <Scheduler
                                                                        width={width}
                                                                        schedulerData={this.state.schedulerData}
                                                                        eventItemTemplateResolver={this.customEventItemTemplate}
                                                                        isTemplateView={true}
                                                                        dateTimeRules={dateTimeRules}
                                                                        t={t}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                    </div>

                                                    <Row className="pt-1 pb-2">
                                                        <Col md={6} className='pe-0'>
                                                            <label className="control-label mb-1">
                                                                {`${t('Select')} ${t('Employee')}`}
                                                            </label>
                                                            <CustomMultiSelect
                                                                options={usersByDepartmentOptions || []}
                                                                value={selectedUserIds || []}
                                                                onChange={this.handleUserSelection}
                                                                className={"custom-multi-select-max-height-150"}
                                                            />
                                                        </Col>
                                                        <Col md={6}>
                                                            <label className="control-label mb-1">
                                                                {`${t('Select')} ${t('Group')}`}
                                                            </label>
                                                            <CurrentUserGroupsWidget
                                                                value={user_group}
                                                                onChange={value => this.handleChangeAndValidate('user_group', value)}
                                                                disabled={!selectedRosterTemplate}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <div className='d-flex'>
                                                        <div className='mt-2 p-2' style={{ backgroundColor: '#f9faf5', borderRadius: '10px', width: '50%' }}>
                                                            <div className='p-2 fw-bold'>{t('Selected Employee List')}</div>
                                                            {this.renderSelectedUsers()}
                                                        </div>
                                                        <div className='d-flex align-items-end justify-content-end mt-2' style={{ width: '51%' }}>
                                                            <Button dynamic_lg className='mx-2' onClick={handleClose}>{t('Cancel')}</Button>
                                                            <Button fill dynamic_lg onClick={this.handleSave}>{t('Save')}</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="published">
                                                <div className='px-3'>
                                                    {this.handleDepartmentAndTemplates(departmentOptions, selectedDepartment, _rosterTemplatesByDepartmentOptions, selectedRosterTemplate)}
                                                    <div className='mt-3 p-2' style={{ backgroundColor: '#f9faf5', borderRadius: '10px', width: '50%' }}>
                                                        <div className='p-2 fw-bold'>{t('Selected Employee List')}</div>
                                                        {this.renderSelectedUsers()}
                                                    </div>
                                                    {message && <i className='mt-2 text-danger'>{message}</i>}
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </div>
                        )}
                    </Modal.Body>
                </Modal>
            </div >
        );
    }
}

export default inject('userStore', 'commonStore', 'scheduleStore', 'clientStore', 'rosterStore')(withLocalization(applicationRouter((observer(RosterShiftViewModal)))));